import React from 'react';
import {
  Container,
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  useTheme,
  Stack
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';

import BG from '../../asserts/ab.png';
import Mission from '../../asserts/mission.webp';
import Vision from '../../asserts/mission.jpg';
import Update from '../../asserts/nursing_update.jpg';
import Nursing from '../../asserts/nursing_service.jpg';
import Footer from '../footer';

export default function AboutUs() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Custom colors for modern light/dark mode distinction
  const missionBg = isDark ? 'rgba(30, 41, 59, 0.3)' : 'rgba(241, 245, 249, 0.6)';
  const visionBg = isDark ? 'rgba(251, 191, 36, 0.03)' : 'rgba(254, 251, 233, 0.6)';
  const chooseUsBg = isDark ? 'rgba(15, 23, 42, 0.4)' : 'rgba(238, 245, 255, 0.4)';

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', transition: 'background-color 0.3s ease' }}>
      
      {/* Hero Banner Section */}
      <Box
        sx={{
          position: 'relative',
          backgroundImage: `url(${BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: { xs: '35vh', md: '45vh' },
          minHeight: '280px',
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: isDark
              ? 'linear-gradient(180deg, rgba(11, 15, 25, 0.8) 0%, rgba(11, 15, 25, 0.95) 100%)'
              : 'linear-gradient(180deg, rgba(30, 58, 138, 0.75) 0%, rgba(30, 58, 138, 0.9) 100%)',
            zIndex: 1,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, py: 4 }}>
          <Box sx={{ maxWidth: '800px', mx: 'auto', textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                color: '#FFFFFF',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3.2rem' },
                lineHeight: 1.3,
                fontFamily: 'Montserrat, Inter, sans-serif',
                textShadow: '0 2px 10px rgba(0,0,0,0.3)',
              }}
            >
              Democratizing Education for All
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: isDark ? 'text.secondary' : 'rgba(255, 255, 255, 0.85)',
                fontWeight: 400,
                fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' },
                lineHeight: 1.6,
                maxWidth: '700px',
                mx: 'auto',
              }}
            >
              Join our educational revolution and enhance your learning experience on India's leading platform. We aim to make quality coaching accessible to every aspiring nurse.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Main Content Area */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Stack spacing={{ xs: 6, md: 10 }}>

          {/* Mission Section */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: '24px',
              bgcolor: missionBg,
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.4)' : '0 10px 30px rgba(0,0,0,0.05)',
              }
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      mb: 3,
                      color: 'primary.main',
                      fontSize: { xs: '1.8rem', md: '2.2rem' }
                    }}
                  >
                    Our Mission
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, fontSize: '1.05rem', lineHeight: 1.7 }}>
                    Inspired by the words of Dr. A.P.J. Abdul Kalam, we believe in laying the foundation for excellence through determination:
                  </Typography>
                  <Stack spacing={2}>
                    {[
                      'Thinking is the capital,',
                      'Enterprise is the way,',
                      'Hard work is the solution.'
                    ].map((text, idx) => (
                      <Stack key={idx} direction="row" spacing={2} alignItems="center">
                        <CheckCircleRoundedIcon sx={{ color: 'success.main', fontSize: '1.4rem' }} />
                        <Typography sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1.1rem' }}>
                          {text}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
                <Box
                  sx={{
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    display: 'flex',
                    justifyContent: 'center',
                    height: '240px',
                    transition: 'transform 0.3s ease',
                    '&:hover': { transform: 'scale(1.02)' }
                  }}
                >
                  <img
                    src={Mission}
                    alt="Our Mission"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Vision Section */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: '24px',
              bgcolor: visionBg,
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.4)' : '0 10px 30px rgba(0,0,0,0.05)',
              }
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    display: 'flex',
                    justifyContent: 'center',
                    height: '280px',
                    transition: 'transform 0.3s ease',
                    '&:hover': { transform: 'scale(1.02)' }
                  }}
                >
                  <img
                    src={Vision}
                    alt="Our Vision"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      mb: 3,
                      color: 'secondary.main',
                      fontSize: { xs: '1.8rem', md: '2.2rem' }
                    }}
                  >
                    Our Vision
                  </Typography>
                  <Stack spacing={3}>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <CheckCircleRoundedIcon sx={{ color: 'secondary.main', mt: 0.3, fontSize: '1.4rem' }} />
                      <Typography sx={{ color: 'text.primary', fontSize: '1rem', lineHeight: 1.6, textAlign: 'justify' }}>
                        Our vision for the nursing coaching institute is to provide comprehensive and innovative coaching services to aspiring nurses across the globe. We aim to create a supportive environment that fosters growth and development, equipping our students with the skills and knowledge they need to excel in their nursing careers.
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <CheckCircleRoundedIcon sx={{ color: 'secondary.main', mt: 0.3, fontSize: '1.4rem' }} />
                      <Typography sx={{ color: 'text.primary', fontSize: '1rem', lineHeight: 1.6, textAlign: 'justify' }}>
                        Our institute will prioritize evidence-based teaching and mentorship, preparing our students to become leaders in their profession. With a commitment to constantly improving and adapting to changing technologies and nursing practices, we envision our institute as a top choice for nursing competitive exams.
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Why Choose Nursing Update Section */}
          <Box sx={{ pt: 2 }}>
            <Box
              sx={{
                mb: 6,
                p: { xs: 4, md: 6 },
                borderRadius: '24px',
                backgroundImage: `url(${Update})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative',
                height: { xs: '200px', md: '280px' },
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
                boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(90deg, rgba(15, 23, 42, 0.9) 0%, rgba(15, 23, 42, 0.5) 100%)',
                  zIndex: 1,
                }
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Typography
                  variant="h3"
                  sx={{
                    color: '#FFFFFF',
                    fontWeight: 800,
                    fontSize: { xs: '1.8rem', md: '2.6rem' },
                    textShadow: '0 2px 10px rgba(0,0,0,0.5)'
                  }}
                >
                  Why Choose Nursing Update?
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    mt: 1,
                    maxWidth: '500px',
                    fontSize: { xs: '0.85rem', md: '1rem' }
                  }}
                >
                  India's leading platform engineered with a forward-thinking outlook, practicality, and custom guidance.
                </Typography>
              </Box>
            </Box>

            {/* Grid of why choose us */}
            <Grid container spacing={3}>
              {[
                {
                  icon: <SchoolRoundedIcon sx={{ fontSize: '2.5rem', color: 'primary.main' }} />,
                  title: 'YouTube to Academy',
                  desc: 'Nursing Update, a unit of Venba Coaching Institute, started as a Youtube channel and received overwhelming response prompting its conversion into a full-fledged coaching institute in 2021. Since then, it has achieved significant success in providing quality coaching and training to nursing students, including high pass rates and successful placements.'
                },
                {
                  icon: <GroupRoundedIcon sx={{ fontSize: '2.5rem', color: 'secondary.main' }} />,
                  title: 'Reputation & Focus',
                  desc: 'The institute has gained a strong reputation for its dedicated faculty, modern teaching methods, and student-focused approach. Our platform provides unparalleled information and expertise, with a forward-thinking outlook, practicality, robust functionalities, and personalized guidance and support throughout the journey.'
                },
                {
                  icon: <WorkspacePremiumRoundedIcon sx={{ fontSize: '2.5rem', color: 'success.main' }} />,
                  title: 'Upgraded Standards',
                  desc: 'As a responsible education provider, we constantly upgrade our knowledge and practices. We have revamped our curriculum and redefined our standards to produce highly qualified, skilled nurses who add value to healthcare systems, enhancing patient care and health outcomes.'
                }
              ].map((item, idx) => (
                <Grid item xs={12} md={4} key={idx}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      p: 2,
                      borderRadius: '20px',
                      bgcolor: 'background.paper',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: isDark ? '0 12px 24px rgba(0,0,0,0.3)' : '0 12px 24px rgba(0,0,0,0.05)',
                        borderColor: 'primary.light'
                      }
                    }}
                  >
                    <CardContent>
                      <Box sx={{ mb: 2 }}>{item.icon}</Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, textAlign: 'justify' }}>
                        {item.desc}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Services Section */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: '24px',
              bgcolor: chooseUsBg,
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 750,
                      mb: 3,
                      color: 'primary.main',
                      fontSize: { xs: '1.8rem', md: '2.2rem' }
                    }}
                  >
                    Our Services
                  </Typography>
                  <Stack spacing={3}>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <TravelExploreRoundedIcon sx={{ color: 'primary.main', mt: 0.3, fontSize: '1.5rem' }} />
                      <Typography sx={{ color: 'text.primary', fontSize: '1.05rem', lineHeight: 1.6, textAlign: 'justify' }}>
                        We specialize in training students for national level nursing exams as well as preparing them for global career opportunities in Dubai, Abu Dhabi, Qatar, UK, Ireland, and Australia.
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <StarRoundedIcon sx={{ color: 'primary.main', mt: 0.3, fontSize: '1.5rem' }} />
                      <Typography sx={{ color: 'text.primary', fontSize: '1.05rem', lineHeight: 1.6, textAlign: 'justify' }}>
                        Our services include free document processing and visa services for working in these countries. Our experienced instructors and consultants provide personalized support to help students achieve their goals and succeed in their careers.
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
                <Box
                  sx={{
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    display: 'flex',
                    justifyContent: 'center',
                    height: '280px',
                    transition: 'transform 0.3s ease',
                    '&:hover': { transform: 'scale(1.02)' }
                  }}
                >
                  <img
                    src={Nursing}
                    alt="Our Services"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>

        </Stack>
      </Container>
      
      <Footer />
    </Box>
  );
}
